<script setup lang="ts" generic="T extends object">
import { useEventListener, useResizeObserver } from '@vueuse/core'
import type { IProps, IUiTableColumn } from './UiTable.types'

const props = withDefaults(defineProps<IProps<T>>(), {
  variant: 'default',
})

const TEXT_ALIGN = {
  start: 'left',
  center: 'center',
  end: 'right',
} as const

const scroller = ref<HTMLElement | null>(null)
const isAtStart = ref(true)
const isAtEnd = ref(true)

const headStyle = (column: IUiTableColumn<T>) => ({
  width: column.width,
  textAlign: TEXT_ALIGN[column.align ?? 'start'],
})

const cellStyle = (column: IUiTableColumn<T>) => ({
  textAlign: TEXT_ALIGN[column.align ?? 'start'],
})

// If the column header has a value, use it; otherwise it comes from the slot
const cellValue = (column: IUiTableColumn<T>, item: T) =>
  column.value ? column.value(item) : item[column.key as keyof T]

const updateScrollState = () => {
  const element = scroller.value

  if (!element) return

  isAtStart.value = element.scrollLeft < 1
  isAtEnd.value = element.scrollLeft > element.scrollWidth - element.clientWidth - 1
}

useEventListener(scroller, 'scroll', updateScrollState, { passive: true })
useResizeObserver(scroller, updateScrollState)
</script>

<template>
  <div
    ref="scroller"
    class="uiTable"
    :class="{
      'uiTable--atStart': isAtStart,
      'uiTable--atEnd': isAtEnd,
    }"
  >
    <table
      class="uiTable__table"
      :class="{ 'uiTable__table--fullWidth': props.variant === 'fullWidth' }"
    >
      <thead class="uiTable__head">
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            class="uiTable__th"
            :style="headStyle(column)"
          >
            <slot
              :name="`head-${column.key}`"
              :column="column"
            >
              {{ column.title }}
            </slot>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(item, index) in items"
          :key="index"
          class="uiTable__tr"
        >
          <td
            v-for="column in columns"
            :key="column.key"
            class="uiTable__td"
            :style="cellStyle(column)"
          >
            <template v-if="$slots[`cell-${column.key}`]">
              <slot
                :name="`cell-${column.key}`"
                :item="item"
              />
            </template>

            <template v-else>
              <p class="uiTable__text">
                {{ cellValue(column, item) }}
              </p>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.uiTable {
  $root: &;

  width: 100%;
  overflow-x: auto;
  border: 0.1rem solid $gray-300;
  border-radius: 1.6rem;

  &:not(#{$root}--atStart) {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:not(#{$root}--atEnd) {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &__table {
    min-width: 64rem;
    width: 100%;
    border-collapse: collapse;

    &--fullWidth {
      min-width: 100%;
    }
  }

  &__head {
    background-color: $gray-100;
  }

  &__th {
    @include h10;
    padding: 1.6rem 2.4rem;
    color: $text-400;
    text-align: left;
    border-bottom: 0.1rem solid $gray-300;

    &:not(:last-child) {
      border-right: 0.1rem solid $gray-300;
    }
  }

  &__td {
    @include body-2;
    padding: 2.4rem;
    color: $text-400;
    vertical-align: top;

    &:not(:last-child) {
      border-right: 0.1rem solid $gray-300;
    }
  }

  &__text {
    @include body-2;
    color: $text-400;
  }

  &__tr:not(:last-child) #{$root}__td {
    border-bottom: 0.1rem solid $gray-300;
  }

  @include atM {
    #{$root}__table--fullWidth {
      min-width: 64rem;
    }
  }
}
</style>
